import { useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';

import {
  HPO_CATEGORIES,
  HPO_CATEGORY_OTHER,
} from '@/features/patient-record/data/hpoCategories';
import { usePhenotypeCategories } from '@/features/patient-record/hooks/usePhenotypeCategories';
import { cn } from '@/lib/utils';
import type { FeatureViewModel } from '@/features/patient-record/types';

interface GroupedFeatureListProps {
  readonly features: readonly FeatureViewModel[];
  readonly renderItem: (feature: FeatureViewModel) => ReactNode;
}

/**
 * Phenotype rows grouped under collapsible body-system headers — the
 * "Group by body system" toggle's render path. Uses
 * {@link usePhenotypeCategories} to resolve each row's HPO term to a
 * curated category root via the vocabulary service's `term_category`
 * field. Categories with no rows are omitted; uncategorised rows fall
 * into an "Other" bucket so nothing disappears.
 *
 * Loading state: while the category resolution is in flight, fall back
 * to a single ungrouped list so rows remain interactable.
 */
export function GroupedFeatureList({
  features,
  renderItem,
}: GroupedFeatureListProps): ReactElement {
  const { t } = useTranslation();
  const hpoIds = useMemo(
    () => features.map((f) => f.hpoId).filter((id) => id.length > 0),
    [features],
  );
  const { data: categoryMap, isLoading } = usePhenotypeCategories(hpoIds);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());

  const groups = useMemo(() => {
    const acc = new Map<string, FeatureViewModel[]>();
    for (const feature of features) {
      const key = categoryMap.get(feature.hpoId) ?? HPO_CATEGORY_OTHER;
      const list = acc.get(key) ?? [];
      list.push(feature);
      acc.set(key, list);
    }
    return acc;
  }, [features, categoryMap]);

  if (isLoading) {
    return (
      <ul className="space-y-0 divide-y divide-border/50">
        {features.map((feature) => (
          <li key={feature.id} className="py-3 first:pt-0 last:pb-0">
            {renderItem(feature)}
          </li>
        ))}
      </ul>
    );
  }

  const orderedKeys = [
    ...HPO_CATEGORIES.map((c) => c.id).filter((id) => groups.has(id)),
    ...(groups.has(HPO_CATEGORY_OTHER) ? [HPO_CATEGORY_OTHER] : []),
  ];

  const labelForKey = (key: string): string => {
    if (key === HPO_CATEGORY_OTHER) return t('patient.phenotypes.categories.other');
    const cat = HPO_CATEGORIES.find((c) => c.id === key);
    return cat !== undefined ? t(`patient.phenotypes.categories.${cat.key}`) : key;
  };

  return (
    <div className="space-y-3">
      {orderedKeys.map((key) => {
        const rows = groups.get(key) ?? [];
        const isCollapsed = collapsed.has(key);
        return (
          <section key={key} className="rounded-md border border-border/60">
            <button
              type="button"
              onClick={() => {
                setCollapsed((prev) => {
                  const next = new Set(prev);
                  if (next.has(key)) next.delete(key);
                  else next.add(key);
                  return next;
                });
              }}
              aria-expanded={!isCollapsed}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-muted/40 hover:bg-muted/60 transition-colors rounded-t-md"
            >
              <span className="flex items-center gap-2 text-xs font-semibold text-card-foreground">
                {isCollapsed ? (
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                )}
                {labelForKey(key)}
              </span>
              <span className="text-[10px] font-semibold tabular-nums text-muted-foreground/80">
                {rows.length}
              </span>
            </button>
            {!isCollapsed && (
              <ul
                className={cn(
                  'space-y-0 divide-y divide-border/50 px-3',
                  rows.length > 0 && 'py-1',
                )}
              >
                {rows.map((feature) => (
                  <li key={feature.id} className="py-2.5 first:pt-2.5 last:pb-2.5">
                    {renderItem(feature)}
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
